import { CaretDown, CaretRight, CalendarBlank, Eye } from 'phosphor-react';
import React, { ReactNode, useMemo, useState } from 'react';
import { formatMoney } from '@dill/dill-shared';
import { DillButton } from '../Dill';
import { ScheduledPaymentDetailsModal } from './ScheduledPaymentDetailsModal';
import { ScheduledPaymentWithNames, formatScheduledDate } from './scheduledPaymentDisplay';

/**
 * Collapsible list of scheduled payments, shared by the buyer and supplier views.
 *
 * Both sides show the same per-payment breakdown, so it lives here rather than being written twice.
 * They differ only in the counterparty name, the framing copy, and whether rows carry actions — a
 * buyer can reschedule or cancel their own payment, a supplier can only watch it.
 */
export const ScheduledPaymentsSection = ({
  title,
  description,
  scheduledPayments,
  isLoading = false,
  counterpartyLabel,
  counterpartyNameFor,
  renderActions,
  defaultExpanded = false,
}: {
  title: string;
  /** Framing shown once the section is open. */
  description?: string;
  scheduledPayments: ScheduledPaymentWithNames[];
  isLoading?: boolean;
  /** Column heading for the other party, e.g. "Supplier" or "Customer". */
  counterpartyLabel: string;
  counterpartyNameFor: (scheduledPayment: ScheduledPaymentWithNames) => string;
  /** Per-row controls. Omitted entirely for read-only views. */
  renderActions?: (scheduledPayment: ScheduledPaymentWithNames) => ReactNode;
  defaultExpanded?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [viewingPayment, setViewingPayment] = useState<ScheduledPaymentWithNames | null>(null);

  const totalAmount = useMemo(
    () =>
      scheduledPayments.reduce(
        (sum, scheduledPayment) => sum + (scheduledPayment.totalAmount || 0),
        0
      ),
    [scheduledPayments]
  );

  // Nothing queued and nothing loading — leave the page exactly as it was before this existed.
  if (scheduledPayments.length === 0 && !isLoading) {
    return null;
  }

  return (
    <div className="flex-shrink-0 mb-3 rounded-xl border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setIsExpanded((previous) => !previous)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        <span className="text-slate-400">
          {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
        </span>
        <span className="text-blue-500">
          <CalendarBlank size={20} weight="duotone" />
        </span>
        <span className="font-semibold text-sm text-slate-800">{title}</span>
        <span className="text-xs text-slate-400">
          {isLoading
            ? 'Loading…'
            : `${scheduledPayments.length} payment${scheduledPayments.length === 1 ? '' : 's'}`}
        </span>
        <span className="ml-auto text-sm font-bold text-slate-900">
          {formatMoney(totalAmount, 2)}
        </span>
      </button>

      {isExpanded && (
        <div className="border-t border-slate-100 px-4 py-3">
          {description && <div className="text-2xs text-slate-400 mb-2">{description}</div>}
          <div className="flex flex-col gap-2">
            {scheduledPayments.map((scheduledPayment) => {
              const invoiceCount = (scheduledPayment.invoicePayments ?? []).length;
              return (
                <div
                  key={scheduledPayment.id}
                  className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setViewingPayment(scheduledPayment)}
                    title="View details"
                    className="flex flex-1 min-w-0 flex-col text-left">
                    <span className="text-xs font-semibold text-slate-800 truncate">
                      {counterpartyNameFor(scheduledPayment)}
                    </span>
                    <span className="text-2xs text-slate-400 mt-0.5">
                      {formatScheduledDate(scheduledPayment.scheduledDate, 'dddd, MMM D, YYYY')}{' '}
                      &middot; {invoiceCount} invoice{invoiceCount === 1 ? '' : 's'}
                      {scheduledPayment.status === 'PROCESSING' && ' · processing'}
                    </span>
                  </button>
                  <div className="text-sm font-bold text-slate-900 shrink-0">
                    {formatMoney(scheduledPayment.totalAmount, 2)}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <DillButton
                      variant="icon-blue"
                      icon={<Eye size={16} />}
                      onClick={() => setViewingPayment(scheduledPayment)}
                    />
                    {renderActions?.(scheduledPayment)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <ScheduledPaymentDetailsModal
        scheduledPayment={viewingPayment}
        counterpartyLabel={counterpartyLabel}
        counterpartyName={viewingPayment ? counterpartyNameFor(viewingPayment) : ''}
        onClose={() => setViewingPayment(null)}
      />
    </div>
  );
};
